
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Form } from '../ui/form';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import StudentBasicInfoFields from './StudentBasicInfoFields';
import StudentContactInfoFields from './StudentContactInfoFields';
import InstitutionInfoFields from './InstitutionInfoFields';
import StudentFormHeader from './StudentFormHeader';
import ProgramList from './ProgramList';
import StudentDocumentsUpload from './StudentDocumentsUpload';
import StudentProfileImageUpload from './StudentProfileImageUpload';
import { useOptimizedStudentFormSubmit } from '../../hooks/useOptimizedStudentFormSubmit';
import { getStudentById } from '../../services/studentService';
import { formSchema, StudentFormData, convertStudentToFormData } from '../../hooks/student-form/types';
import { useDefaultValues } from '../../hooks/student-form/useDefaultValues';
import { useStudents } from "@/contexts";

export const StudentFormContent = React.memo(({ studentId }: { studentId?: string }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(!!studentId);
  const { defaultValues } = useDefaultValues();
  const { programCategories, programsByCategory } = useStudents();
  
  // Get optimized submission handler
  const { handleSubmit, isSubmitting } = useOptimizedStudentFormSubmit();
  
  // Initialize form with react-hook-form
  const form = useForm<StudentFormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange',
  });

  // Load student data if editing - memoized to prevent unnecessary calls
  const loadStudentData = useCallback(async () => {
    if (!studentId) return;
    
    try {
      setIsLoading(true);
      const data = await getStudentById(studentId);
      if (data) {
        const formattedData = convertStudentToFormData(data);
        form.reset(formattedData);
      }
    } catch (error) {
      console.error("Error loading student data:", error);
      toast.error("Failed to load student data");
    } finally {
      setIsLoading(false);
    }
  }, [studentId, form]);

  useEffect(() => {
    if (studentId) {
      loadStudentData();
    }
  }, [studentId, loadStudentData]);

  // Optimized form submission
  const onSubmit = useCallback(async (data: StudentFormData) => {
    if (isSubmitting) return;
    
    try {
      console.log('Form submission started:', data.name);
      
      // Quick validation
      if (!data.name?.trim()) {
        toast.error('Student name is required');
        return;
      }
      
      if (!data.programs?.length) {
        toast.error('At least one program is required');
        return;
      }
      
      // Ensure we have the ID if editing
      if (studentId) {
        data.id = parseInt(studentId, 10);
      }
      
      // Submit the form data
      await handleSubmit(data);
      
    } catch (error) {
      console.error('Form submission error:', error);
      // Error is already handled in handleSubmit
    }
  }, [isSubmitting, studentId, handleSubmit]);

  // Memoize components to prevent unnecessary re-renders
  const memoizedHeader = useMemo(() => 
    <StudentFormHeader isEditing={!!studentId} />, 
    [studentId]
  );

  const memoizedProgramList = useMemo(() => 
    <ProgramList 
      programCategories={programCategories} 
      programsByCategory={programsByCategory} 
    />, 
    [programCategories, programsByCategory]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {memoizedHeader}
        
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="p-6">
            <div className="mb-6 flex flex-col items-center">
              <StudentProfileImageUpload />
            </div>
            <StudentBasicInfoFields />
          </Card>
          
          <Card className="p-6">
            <StudentContactInfoFields />
          </Card>
        </div>
        
        <Card className="p-6">
          <InstitutionInfoFields />
        </Card>
        
        <Card className="p-6">
          {memoizedProgramList}
        </Card>
        
        <StudentDocumentsUpload />
        
        <div className="flex justify-end gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/students')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : studentId ? 'Update Student' : 'Create Student'}
          </Button>
        </div>
      </form>
    </Form>
  );
});

StudentFormContent.displayName = 'StudentFormContent';
